import {
  AuthChallenge,
  AuthError,
  LoginCredentials,
  UserInfo,
} from '@ptek/shared-auth-sdk';
import {
  AuthProviderProps,
  AuthProvider as SharedAuthProvider,
  useAuth,
} from '@ptek/shared-auth-sdk/react';
import React, { Component, createContext, useContext, ReactNode } from 'react';

export interface WidgetAuthContextType {
  user: UserInfo;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: AuthError | null;
  challenge: AuthChallenge | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  clearError: () => void;
  clearChallenge: () => void;
  completeNewPassword: (username: string, newPassword: string) => Promise<void>;
  tenantId?: string;
  requireAuth: boolean;
  widgetPermissions: string[];
}

const WidgetAuthContext = createContext<WidgetAuthContextType | null>(null);

interface WidgetAuthProviderProps extends AuthProviderProps {
  tenantId?: string;
  requireAuth?: boolean;
  widgetPermissions?: string[];
}

/**
 * Error boundary to handle HMR-related auth context errors in development.
 * During HMR, the auth context may be temporarily unavailable.
 */
interface HmrErrorBoundaryState {
  hasError: boolean;
}

class HmrAuthErrorBoundary extends Component<
  { children: ReactNode },
  HmrErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): HmrErrorBoundaryState {
    // Always return hasError: true - componentDidCatch will decide whether to re-throw
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    const isHmrAuthError =
      process.env.NODE_ENV === 'development' &&
      error.message?.includes(
        'useAuthContext must be used within an AuthProvider'
      );

    if (isHmrAuthError) {
      // Silently recover during HMR - the error boundary will auto-retry
      return;
    }

    // Re-throw non-HMR errors so they propagate normally
    throw error;
  }

  componentDidUpdate(
    _: { children: ReactNode },
    prevState: HmrErrorBoundaryState
  ) {
    if (this.state.hasError && !prevState.hasError) {
      setTimeout(() => {
        this.setState({ hasError: false });
      }, 100);
    }
  }

  render() {
    if (this.state.hasError) {
      // Return null during HMR recovery - will auto-retry
      return null;
    }
    return this.props.children;
  }
}

/**
 * Inner provider that wraps useAuth from shared-auth-sdk.
 */
const InnerProvider: React.FC<Omit<WidgetAuthProviderProps, 'config'>> = ({
  children,
  tenantId,
  requireAuth = false,
  widgetPermissions = [],
}) => {
  const sharedAuth = useAuth();
  const value: WidgetAuthContextType = {
    ...sharedAuth,
    user: sharedAuth.user!,
    tenantId,
    requireAuth,
    widgetPermissions,
  };
  return (
    <WidgetAuthContext.Provider value={value}>
      {children}
    </WidgetAuthContext.Provider>
  );
};

export const WidgetAuthProvider: React.FC<WidgetAuthProviderProps> = ({
  children,
  tenantId,
  requireAuth,
  widgetPermissions,
  ...props
}) => (
  <SharedAuthProvider {...props}>
    <HmrAuthErrorBoundary>
      <InnerProvider
        tenantId={tenantId}
        requireAuth={requireAuth}
        widgetPermissions={widgetPermissions}
      >
        {children}
      </InnerProvider>
    </HmrAuthErrorBoundary>
  </SharedAuthProvider>
);

// eslint-disable-next-line react-refresh/only-export-components
export const useWidgetAuth = (): WidgetAuthContextType => {
  const ctx = useContext(WidgetAuthContext);
  if (!ctx) {
    throw new Error('useWidgetAuth must be used within a WidgetAuthProvider');
  }
  return ctx;
};
